<template>
  <ul class="visit-checklist">
    <VisitStepRow
      :class="{
        'visit-checklist__visit-step': true,
        'visit-checklist__visit-step--hidden': visitStep.text.toLowerCase() === 'upcoming actions',
      }"
      v-for="(visitStep, index) in checklistRows"
      :key="visitStep.text"
      :allSteps="checklistRows"
      :index="index"
      :isExpandable="visitStep.isCompedActivitySection || visitStep.isLodgingSection || visitStep.isVisitPlanSection"
      :isExpanded="
        (visitStep.isLodgingSection && !props.lodgingDetailsCollapsed) ||
        (visitStep.isCompedActivitySection && !props.compDetailsCollapsed) ||
        (visitStep.isVisitPlanSection && !props.isVisitPlanCollapsed)
      "
      @toggle="
        () => {
          if (visitStep.isCompedActivitySection) emit('update:compDetailsCollapsed', !props.compDetailsCollapsed);
          if (visitStep.isLodgingSection) emit('update:lodgingDetailsCollapsed', !props.lodgingDetailsCollapsed);
          if (visitStep.isVisitPlanSection) emit('update:isVisitPlanCollapsed', !props.isVisitPlanCollapsed);
        }
      "
    >
      <!-- Lodging Section -->
      <div
        v-if="visitStep.isLodgingSection"
        :style="{
          opacity: props.isSavingCollabInput ? '0.5' : 1,
          pointerEvents: props.isSavingCollabInput ? 'none' : 'auto',
        }"
        class="form-container"
      >
        <!--Mimic the logic in CreateCollab (to decide if they see the lodging step)-->
        <template v-if="collabRemainingCounts && collabRemainingCounts?.showLodgingStep !== false">
          <!-- Note: Similar in StepLodging.vue -->
          <div class="global-select-wrap" style="margin-bottom: 10px">
            <!--NOTE! Any changes here update StepLodging.vue also!-->
            <!-- Grandfathered: nights choice only -->
            <select
              v-if="props.collabInput.hotelCoverageUsed === 'Grandfathered'"
              class="global-select"
              ref="hotelCovered"
              v-model="props.collabInput.hotelNightsCovered"
              :disabled="props.isCompleted"
              @change="requestCollabInputSave"
            >
              <option :value="0">Can't arrange Lodging</option>
              <option v-if="canChooseArrange1Night" :value="1">Can arrange a 1-night stay</option>
              <option :value="2">Can arrange a 2-night stay</option>
            </select>
            <!-- Coverage-driven choice: paid add-on and NoCoverage both use 0 nights -->
            <select
              v-else
              class="global-select"
              ref="hotelCovered"
              v-model="lodgingChoiceKey"
              :disabled="props.isCompleted"
              @change="
                () => {
                  onLodgingChoiceChanged();
                  requestCollabInputSave();
                }
              "
            >
              <option v-if="canChoosePaidAddOn" value="paidAddOn">Use Paid Lodging Add-on</option>
              <option v-if="canChooseNoCoverage" value="noCoverage">No Lodging Coverage</option>
              <option v-if="canChooseArrange1Night" value="arrange1">Can arrange a 1-night stay</option>
              <option value="arrange2">Can arrange a 2-night stay</option>
            </select>

            <div class="global-select-wrap__arrow">&nbsp;</div>
          </div>

          <!--<NoteWithIcon v-if="props.collabInput.hotelCoverageUsed === 'NoCoverage'" size="tiny" color="yellow" style="margin-bottom: 10px">
            <template #icon><IconEmbedded name="info-simple_4" /></template>
            <template #text>
              <span>No lodging coverage will reduce the number of creators that apply for this visit.</span>
            </template>
          </NoteWithIcon>-->

          <template v-if="props.collabInput.hotelNightsCovered > 0">
            <!-- Who to book for (so they don't have to dig through Messages for the creator's details) -->
            <SrpDropdown v-if="isCreatorChosen && props.creatorDetails" class="book-for-row" placement="bottom-start" :offset="6" width="auto">
              <template #clickableElement="{ isDropdownVisible }">
                <button type="button" class="book-for-row__button" :aria-expanded="isDropdownVisible" title="Creator booking info">
                  <span class="book-for-row__name">{{ creatorBookingName }}</span>
                  <span class="book-for-row__icons">
                    <IconEmbedded name="envelope_2" :size="17" color="rgba(0, 0, 0, 0.45)" />
                    <IconEmbedded v-if="props.creatorDetails.phoneNumber" name="phone_2-5" :size="17" color="rgba(0, 0, 0, 0.45)" />
                  </span>
                </button>
              </template>
              <template #dropdown="{ close }">
                <ul class="creator-booking-info" @keydown.esc.stop="close()">
                  <li class="creator-booking-info__line creator-booking-info__line--name">
                    <CopyTextNew :text="creatorBookingName">{{ creatorBookingName }}</CopyTextNew>
                  </li>
                  <li v-if="visitDatesText" class="creator-booking-info__line">
                    <IconEmbedded class="creator-booking-info__icon" name="calendar_2" :size="16" color="rgba(0, 0, 0, 0.4)" />
                    <span>Visiting {{ visitDatesText }}</span>
                  </li>
                  <li v-if="props.creatorDetails.email" class="creator-booking-info__line">
                    <IconEmbedded class="creator-booking-info__icon" name="envelope_2" :size="16" color="rgba(0, 0, 0, 0.4)" />
                    <CopyTextNew :text="props.creatorDetails.email">{{ props.creatorDetails.email }}</CopyTextNew>
                  </li>
                  <li v-if="props.creatorDetails.phoneNumber" class="creator-booking-info__line">
                    <IconEmbedded class="creator-booking-info__icon" name="phone_2-5" :size="16" color="rgba(0, 0, 0, 0.4)" />
                    <CopyTextNew :text="props.creatorDetails.phoneNumber">{{ props.creatorDetails.phoneNumber }}</CopyTextNew>
                  </li>
                </ul>
              </template>
            </SrpDropdown>

            <div class="ui input" style="width: 100%; margin-bottom: 10px">
              <input type="text" v-model="props.collabInput.hotelName" placeholder="Lodging name" :disabled="props.isCompleted" @change="requestCollabInputSave" />
            </div>

            <div class="ui input" style="width: 100%; margin-bottom: 10px">
              <input type="text" v-model="props.collabInput.hotelDateBooked" placeholder="Lodging dates" :disabled="props.isCompleted" @change="requestCollabInputSave" />
            </div>

            <!-- A confirmation # only exists once a creator is chosen and booked -->
            <div v-if="isCreatorChosen" class="conf-row">
              <div class="ui input conf-row__input">
                <input type="text" maxlength="200" v-model="props.collabInput.hotelConf" placeholder="Confirmation #" :disabled="props.isCompleted" @change="requestCollabInputSave" />
              </div>
              <!-- Files go through Messages (it already supports attachments) -->
              <TooltipWrapper v-if="!props.isCompleted" :width="210">
                <template #hoverableElement>
                  <button type="button" class="send-file-button" :aria-label="`Send ${creatorNameOrFallback} a confirmation file in Messages`" @click="emit('openChat')">
                    <IconEmbedded name="paperclip_2" :size="18" color="rgba(0, 0, 0, 0.5)" />
                  </button>
                </template>
                <template #tooltip>Have a confirmation file? Send it to {{ creatorNameOrFallback }} in Messages</template>
              </TooltipWrapper>
            </div>

            <!-- We let them save the above field but this doesn't make sense to set until a creator has been chosen -->
            <div v-if="!props.isCompleted && props.creatorInput && !props.collabInput.hotelIsBooked" class="lodging-checkbox-n-info">
              <SrpButton
                class="lodging-checkbox-n-info__checkbox-component"
                :size="screenSize === 'tablet' ? 'tiny' : 'small'"
                color="orange"
                :isDisabled="props.collabInput.hotelIsBooked"
                @click="
                  () => {
                    props.collabInput.hotelIsBooked = true;
                    requestCollabInputSave();
                  }
                "
                :style="{ maxWidth: screenSize === 'tablet' ? '185px' : 'unset' }"
              >
                Mark Lodging Coordinated
              </SrpButton>

              <InfoIconWithTooltip class="lodging-checkbox-n-info__tooltip-wrap">Check this when the lodging has been fully arranged for the creator.</InfoIconWithTooltip>
            </div>

            <div v-else-if="props.collabInput.hotelIsBooked" style="display: flex; align-items: center">
              <div class="lodging-checkbox-n-info" style="margin-right: 10px">
                <IconEmbedded name="check_4-5" :size="14" style="margin-right: 4px" color="rgba(61, 133, 40, 1)" />
                Lodging is coordinated
              </div>

              <LinkWithIcon
                v-if="!props.isCompleted"
                isDottedUnderline
                style="position: relative; top: -1px"
                @click="
                  () => {
                    props.collabInput.hotelIsBooked = false;
                    requestCollabInputSave();
                  }
                "
              >
                <span>undo</span>
              </LinkWithIcon>
            </div>

            <!-- Destinations often book but forget to mark it, so nudge once they've recorded a Conf # -->
            <div v-if="showLodgingNudge" class="mark-coordinated-nudge">Booked? Mark it coordinated so {{ creatorNameOrFallback }} is notified.</div>
          </template>
        </template>

        <!-- Creator Hotel covered -->
        <div v-if="creatorCoveringHotel" class="field">
          <div><b>Creator's Lodging Details</b></div>
          <div v-if="props.creatorInput?.nightsRequired === 0">None required</div>
          <div v-else-if="props.creatorInput">
            <div v-if="props.creatorInput.hotelName?.length > 0">{{ props.creatorInput.hotelName }}</div>
            <div v-if="props.creatorInput.hotelDateBooked?.length > 0">{{ props.creatorInput.hotelDateBooked }}</div>
            <div v-if="!props.creatorInput.hotelName && !props.creatorInput.hotelDateBooked">Creator hasn't filled out lodging details.</div>
          </div>
        </div>
      </div>
      <!-- / Lodging Section -->

      <!-- Comped Activities Section -->
      <div
        v-else-if="visitStep.isCompedActivitySection"
        :style="{
          opacity: props.isSavingCollabInput ? '0.5' : 1,
          pointerEvents: props.isSavingCollabInput ? 'none' : 'auto',
        }"
        class="form-container"
      >
        <div>Any activities you would like to assist with:</div>
        <CollabCompTags :collabInput="props.collabInput" :disabled="props.isCompleted" @change="requestCollabInputSave" />

        <!-- Only show the below if a tag is checked -->
        <div v-if="willCompActivities" class="ui form field">
          <textarea
            class="comps-notes-textarea"
            type="text"
            maxlength="1000"
            v-model="props.collabInput.bigTicketItemBookinNotes"
            placeholder="Ex. Meal at the Hungry Gator"
            :disabled="props.isCompleted"
            @change="requestCollabInputSave"
          ></textarea>
        </div>

        <!-- Reservation details only make sense once a creator is chosen -->
        <template v-if="willCompActivities && isCreatorChosen">
          <div class="ui form field" style="margin-bottom: 10px">
            <div class="booking-detail-label-row">
              <label class="booking-detail-label" for="visit-checklist-comps-booking-detail">Booking details</label>
              <TooltipWrapper v-if="!props.isCompleted" :width="210">
                <template #hoverableElement>
                  <button type="button" class="send-file-button" :aria-label="`Send ${creatorNameOrFallback} a voucher or confirmation file in Messages`" @click="emit('openChat')">
                    <IconEmbedded name="paperclip_2" :size="18" color="rgba(0, 0, 0, 0.5)" />
                  </button>
                </template>
                <template #tooltip>Have a voucher or confirmation file? Send it to {{ creatorNameOrFallback }} in Messages</template>
              </TooltipWrapper>
            </div>
            <textarea
              id="visit-checklist-comps-booking-detail"
              class="booking-detail-textarea"
              rows="2"
              maxlength="1000"
              v-model="props.collabInput.compsBookingDetail"
              placeholder="Times, conf #s, who to ask for"
              :disabled="props.isCompleted"
              @change="requestCollabInputSave"
            ></textarea>
          </div>
        </template>

        <!-- We let them save the above field but this doesn't make sense to set until a creator has been chosen -->
        <div v-if="!props.isCompleted && props.creatorInput && willCompActivities && !props.collabInput.bigTicketItemsAllBooked" class="lodging-checkbox-n-info" style="width: 190px">
          <SrpButton
            class="lodging-checkbox-n-info__checkbox-component"
            :size="screenSize === 'tablet' ? 'tiny' : 'small'"
            color="orange"
            @click="
              () => {
                props.collabInput.bigTicketItemsAllBooked = true;
                requestCollabInputSave();
              }
            "
            :style="{ maxWidth: screenSize === 'tablet' ? '185px' : 'unset' }"
          >
            Mark Activities Coordinated
          </SrpButton>

          <InfoIconWithTooltip class="lodging-checkbox-n-info__tooltip-wrap">Check this when activities have been coordinated for the creator.</InfoIconWithTooltip>
        </div>

        <div v-else-if="props.collabInput.bigTicketItemsAllBooked" style="display: flex; align-items: center">
          <div class="lodging-checkbox-n-info" style="margin-right: 10px">
            <IconEmbedded name="check_4-5" :size="14" style="margin-right: 4px" color="rgba(61, 133, 40, 1)" />
            Activities are coordinated
          </div>

          <LinkWithIcon
            v-if="!props.isCompleted"
            isDottedUnderline
            style="position: relative; top: -1px"
            @click="
              () => {
                props.collabInput.bigTicketItemsAllBooked = false;
                requestCollabInputSave();
              }
            "
          >
            <span>undo</span>
          </LinkWithIcon>
        </div>

        <div v-if="showCompsNudge" class="mark-coordinated-nudge">Booked? Mark it coordinated so {{ creatorNameOrFallback }} is notified.</div>
      </div>
      <!-- / Comped Activities Section -->

      <!-- Visit plan section -->
      <div v-else-if="visitStep.isVisitPlanSection">
        <!-- Plan status line -->
        <div v-if="!props.creatorInput?.visitPlanSentDate && !props.creatorInput?.visitPlanSignOffDate" class="plan-status-line" style="margin-bottom: 9px">
          <div class="plan-status-line__circle"></div>
          <div class="plan-status-line__text">Not yet received from the creator</div>
        </div>
        <!-- / Plan status line -->

        <!-- Plan status line -->
        <div v-else-if="props.creatorInput?.visitPlanSentDate && !props.creatorInput?.visitPlanSignOffDate" class="plan-status-line plan-status-line--orange" style="margin-bottom: 9px">
          <div class="plan-status-line__circle"></div>
          <div class="plan-status-line__text">Waiting to be reviewed</div>
        </div>
        <!-- / Plan status line -->

        <!-- Plan status line -->
        <div v-else-if="props.creatorInput?.visitPlanSignOffDate" class="plan-status-line plan-status-line--green" style="margin-bottom: 9px">
          <div class="plan-status-line__circle"></div>
          <div class="plan-status-line__text">Approved</div>
        </div>
        <!-- / Plan status line -->

        <template v-if="props.creatorInput?.visitPlanSentDate">
          <SrpButton v-if="props.creatorInput?.visitPlanSignOffDate" size="small" @click="isVisitPlanApprovalModalVisible = true"> View </SrpButton>
          <SrpButton v-else color="orange" size="small" @click="isVisitPlanApprovalModalVisible = true"> Review </SrpButton>
        </template>
      </div>
      <!-- / Visit plan section -->
    </VisitStepRow>

    <VisitPlanApprovalModal
      v-model:isVisible="isVisitPlanApprovalModalVisible"
      @connectWithTheCreator="
        () => {
          isVisitPlanApprovalModalVisible = false;
          emit('openChat');
        }
      "
      :planText="props.collabInput.useLocationsInVisitPlan ? collabLocationsStore.locationsTextRepresentation : props.creatorInput?.visitPlan"
      :signOffDate="props.creatorInput?.visitPlanSignOffDate"
      :communityId="props.communityId"
      :collabInputId="props.collabInput.id"
      :requestedLocationCount="props.collabInput?.requestedLocationCount"
      :requestedAdventureCount="props.collabInput?.requestedAdventureCount"
      :customerOwnedAdventures="props.collabInput?.collaboration?.customerOwnedAdventures"
      @signOff="signOff"
      :creatorName="props.creatorName"
    />
  </ul>
</template>

<!-- prettier-ignore -->
<script lang="ts">export default { name: "VisitCheckList" };</script>

<script setup lang="ts">
import Logger from "@helpers/Logger";
import { ref, computed, onMounted, watch, inject, Ref } from "vue";
import { RouteHelper } from "@helpers/RouteHelper";

// Types
import { CollabChecklistRow } from "@contracts/collabChecklist";
import { CollabAssignedCreator, CollabCreatorInput, CollabInput, CustomerCollabRemainingCounts, DefaultLodgingNightsRequired } from "@contracts/collab";
import { ScreenSize } from "@contracts/screenSize";
import { usesShrpaLodgingStipendFromCollab } from "@logic/CollabLodgingHelpers";
import DateUtils from "@logic/DateUtils";

// Components
import CollabCompTags from "@views/PaidCollab/CollabCompTags.vue";
import CopyTextNew from "@components/CopyTextNew.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import VisitPlanApprovalModal from "@views/PaidCollab/CollabSummary/VisitCheckList/VisitPlanApprovalModal.vue";
import VisitStepRow from "./VisitStepRow.vue";

// Stores
import { useCollabLocationsStore } from "@stores/collabLocations";
import { useToastsStore } from "@stores/toasts";
const collabLocationsStore = useCollabLocationsStore();
const toastsStore = useToastsStore();

const props = withDefaults(
  defineProps<{
    checklistRows: Array<CollabChecklistRow>;
    lodgingDetailsCollapsed: boolean;
    collabInput: CollabInput | null;
    creatorInput: CollabCreatorInput | null;
    collabRemainingCounts: CustomerCollabRemainingCounts | null;
    adventuresAreCreated: boolean;
    compDetailsCollapsed: boolean;
    isVisitPlanCollapsed: boolean;
    calculateChecklist?: Function;
    communityId: string;
    creatorName: string;
    // Full name, email and phone of the chosen creator (shown in the lodging "Book for" pop-up)
    creatorDetails?: CollabAssignedCreator | null;
    isSavingCollabInput?: boolean;
    isCompleted?: boolean;
  }>(),
  {
    checklistRows: () => [],
    lodgingDetailsCollapsed: false,
    collabInput: null,
    creatorInput: null,
    collabRemainingCounts: null,
    adventuresAreCreated: false,
    compDetailsCollapsed: false,
    calculateChecklist: () => {},
    communityId: "",
    creatorName: "",
    creatorDetails: null,
    isSavingCollabInput: false,
    isCompleted: false,
  }
);

const emit = defineEmits<{
  (e: "update:lodgingDetailsCollapsed", value: boolean): void;
  (e: "update:compDetailsCollapsed", value: boolean): void;
  (e: "update:isVisitPlanCollapsed", value: boolean): void;
  (e: "save"): void;
  (e: "openChat"): void;
  (e: "requestCollabInputSave"): void;
}>();

const screenSize = inject("screenSize") as Ref<ScreenSize>;

// ============================================================================
const creatorCoveringHotel = computed<boolean>(() => {
  return usesShrpaLodgingStipendFromCollab(props.collabInput) && props.creatorInput != null;
});

const willCompActivities = computed<boolean>(() => {
  return props.collabInput?.compedActivitiesTags?.length > 0;
});

// Booking helpers ============================================================
// Same gate as the Mark Coordinated buttons: nothing can be booked until a creator is chosen
const isCreatorChosen = computed<boolean>(() => props.creatorInput != null);

const creatorNameOrFallback = computed<string>(() => props.creatorName || "the creator");

// Hotels need the legal name, so prefer the full name over the display name
const creatorBookingName = computed<string>(() => props.creatorDetails?.fullName?.trim() || props.creatorDetails?.firstName || "");

const visitDatesText = computed<string>(() => {
  if (!props.creatorInput?.proposedVisitDateTime) return "";
  return DateUtils.formatDateRange(props.creatorInput.proposedVisitDateTime, props.creatorInput.numberOfDaysStaying, false);
});

// Destinations often book but never click Mark Coordinated, so once they've recorded booking details, prompt them to mark it
const showLodgingNudge = computed<boolean>(() => {
  return !props.isCompleted && isCreatorChosen.value && !props.collabInput?.hotelIsBooked && !!props.collabInput?.hotelConf?.trim();
});
const showCompsNudge = computed<boolean>(() => {
  return !props.isCompleted && isCreatorChosen.value && willCompActivities.value && !props.collabInput?.bigTicketItemsAllBooked && !!props.collabInput?.compsBookingDetail?.trim();
});

const canChoosePaidAddOn = computed<boolean>(() => {
  return (props.collabRemainingCounts?.collabHotelsPaidRemaining ?? 0) > 0 || props.collabInput.hotelCoverageUsed === "CustomerChosePaidAddOn" || props.collabInput.hotelCoverageUsed === "PaidAddOn";
});

const canChooseNoCoverage = computed<boolean>(() => {
  return props.collabRemainingCounts?.collabMinHotelNights === 0 || props.collabInput.hotelCoverageUsed === "NoCoverage";
});

const canChooseArrange1Night = computed<boolean>(() => {
  const minNights = props.collabRemainingCounts?.collabMinHotelNights ?? DefaultLodgingNightsRequired;
  return minNights <= 1 || props.collabInput.hotelNightsCovered === 1;
});

const lodgingChoiceKey = computed({
  get(): string {
    // NOTE! Mirrored in StepLodging!
    const used = props.collabInput.hotelCoverageUsed;
    const nights = props.collabInput.hotelNightsCovered;
    if (used === "CustomerChosePaidAddOn" || used === "PaidAddOn") return "paidAddOn";
    // Destination-arranged nights (1 or 2) are never NoCoverage
    if (nights === 1) return "arrange1";
    if (used === "CustomerChoseToArrange" || (nights ?? 0) > 0) return "arrange2";
    if (used === "NoCoverage") return "noCoverage";
    return "arrange2";
  },
  set(value: string) {
    applyLodgingChoice(value);
  },
});

// Hotel Coverage ==============================================================
function applyLodgingChoice(choice: string) {
  // NOTE! Mirrored in StepLodging!
  // arrange1/arrange2 = destination arranges lodging (CustomerChoseToArrange), never NoCoverage
  if (choice === "paidAddOn") {
    props.collabInput.hotelCoverageUsed = "CustomerChosePaidAddOn";
    props.collabInput.hotelNightsCovered = 0;
    props.collabInput.hotelEstimatedValue = null;
  } else if (choice === "noCoverage") {
    props.collabInput.hotelCoverageUsed = "NoCoverage";
    props.collabInput.hotelNightsCovered = 0;
    props.collabInput.hotelEstimatedValue = null;
  } else if (choice === "arrange1") {
    props.collabInput.hotelCoverageUsed = "CustomerChoseToArrange";
    props.collabInput.hotelNightsCovered = 1;
  } else {
    props.collabInput.hotelCoverageUsed = "CustomerChoseToArrange";
    props.collabInput.hotelNightsCovered = DefaultLodgingNightsRequired;
  }
  Logger.info("VisitCheckList onLodgingChoiceChanged", props.collabInput.hotelCoverageUsed, props.collabInput.hotelNightsCovered);
}

function onLodgingChoiceChanged() {
  // v-model setter already applied the choice; keep for explicit @change clarity
}

// SignOff ====================================================================

async function signOff() {
  // Reloading the creatorInput is problematic because we don't use stores. Therefore, it's easier to manually update the visitPlanSignOffDate field with a value that is almost identical to the one in the database
  // NOTE: Refactor creatorInput to utilize a centralized store and populate the 'visitPlanSignOffDate' field with the data returned from the endpoint to avoid property mutation.
  props.creatorInput.visitPlanSignOffDate = new Date().toISOString();
  props.calculateChecklist();
}

// Toggle Visit Plan modal ====================================================
const isVisitPlanApprovalModalVisible = ref(false);

watch(
  () => props.creatorInput,
  () => {
    checkForVisitPlanShow();
  }
);

function checkForVisitPlanShow() {
  if (RouteHelper.getHashParam("isReviewVisitPlanModalVisible") && props.creatorInput?.visitPlanSentDate && !props.creatorInput?.visitPlanSignOffDate) {
    console.info("Auto-opening the visit plan modal");
    isVisitPlanApprovalModalVisible.value = true;
  }
}

// Load collab locations data =================================================
onMounted(async () => {
  // Repair inconsistent state: arranged nights must not stay on NoCoverage
  if ((props.collabInput?.hotelNightsCovered ?? 0) > 0 && props.collabInput.hotelCoverageUsed === "NoCoverage") {
    props.collabInput.hotelCoverageUsed = "CustomerChoseToArrange";
  }
  if (props.collabInput?.id && props.creatorInput?.creatorId && props.communityId) {
    await collabLocationsStore.loadCollabLocationsData(props.collabInput.id, props.creatorInput?.creatorId, props.communityId);
  }
});

watch(
  () => [props.creatorInput, props.collabInput, props.communityId],
  async () => {
    await collabLocationsStore.loadCollabLocationsData(props.collabInput.id, props.creatorInput?.creatorId, props.communityId);
  }
);

// Request collab input save ==================================================
function requestCollabInputSave() {
  emit("requestCollabInputSave");
  toastsStore.success({ message: "Changes saved", expiresInMs: 2000 });
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Form container =============================================================
.form-container {
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .form-container {
    max-width: 175px;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
}

// Plan status line ===========================================================
.plan-status-line {
  display: flex;
  align-items: center;
  color: #666666;
  font-weight: bold;

  &__circle {
    width: 7px;
    height: 7px;
    margin: -1px 5px 0 0;
    border-radius: 100px;
    background: #666666;
  }

  &--green {
    color: #3d8528;

    .plan-status-line__circle {
      background: #3d8528;
    }
  }
  &--orange {
    color: #bb9227;

    .plan-status-line__circle {
      background: #bb9227;
    }
  }

  &__text {
  }
}

// Info icon ==================================================================
.info-icon {
  min-width: 23px;
  min-height: 23px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: pointer;

  &::before {
    content: "";
    width: 100%;
    height: 100%;
    border-radius: 100px;
    position: absolute;
    inset: 0 auto auto 0;
    background: rgba(0, 0, 0, 0.1);
    transition:
      width 0.05s ease-in-out,
      height 0.05s ease-in-out,
      top 0.05s ease-in-out,
      left 0.05s ease-in-out;
  }

  &::after {
    content: "";
    width: calc(100% + 22px);
    height: calc(100% + 22px);
    position: absolute;
    inset: 50% auto auto 50%;
    transform: translate(-50%, -50%);
  }

  &--expanded {
    &::before {
      width: calc(100% + 6px);
      height: calc(100% + 6px);
      inset: -3px auto auto -3px;
    }
  }

  &__symbol {
  }
}

// Lodging checkbox & info ====================================================
.lodging-checkbox-n-info {
  display: inline-flex;
  align-items: center;
  position: relative;
  white-space: nowrap;

  &__checkbox-component {
    margin-right: 8px;
  }

  &__tooltip-wrap {
  }

  &__info-icon {
  }
}

// Book for row ===============================================================
.book-for-row {
  margin-bottom: 10px;

  &__button {
    max-width: 100%;
    padding: 0;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-align: left;
    color: #5b5b5b;
    font: inherit;
    background: none;
    cursor: pointer;
  }

  // Single line: a long name truncates rather than pushing the icons onto a second line
  &__name {
    min-width: 0;
    overflow: hidden;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    // Same "dotted" underline as LinkWithIcon's isDottedUnderline (it's actually dashed; true dotted packs the dots too tight)
    text-decoration: underline;
    text-decoration-color: rgba(91, 91, 91, 0.5);
    text-decoration-style: dashed;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
  }

  &__icons {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}

// Conf # row (input + attach icon) ===========================================
.conf-row {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  &__input {
    min-width: 0;
    flex: 1;
  }
}

// Send file (attach) icon button =============================================
.send-file-button {
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.07);
  }
}

.booking-detail-label-row {
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 4px;
}

// Creator booking info (pop-up) ==============================================
// Sized to its content (long names/emails widen it), capped so it can't run off the screen; past the cap, text wraps
.creator-booking-info {
  min-width: 230px;
  max-width: min(400px, calc(100vw - 32px));
  padding: 14px 16px;
  margin: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
  color: #444;

  &__line {
    display: flex;
    align-items: center;
    gap: 8px;
    word-break: break-word;

    &--name {
      font-weight: 600;
      font-size: 15px;
    }
  }

  &__icon {
    flex-shrink: 0;
  }
}

// Booking detail label =======================================================
// Comps notes: ~30% shorter than the global textarea default (168px); still resizable
.ui.form .comps-notes-textarea {
  width: 100%;
  height: 118px;
  min-height: 60px;
  margin: 15px 0;
}

// A global textarea rule sets a fixed height, so rows alone doesn't size it (about 2 lines; still resizable)
.ui.form .booking-detail-textarea {
  width: 100%;
  height: 60px;
  min-height: 60px;
}

// width: auto overrides Semantic's full-width field label, so the attach icon sits right after the text
.booking-detail-label {
  width: auto !important;
  margin: 0 !important;
  display: block;
  font-weight: 600;
}

// Mark coordinated nudge =====================================================
.mark-coordinated-nudge {
  margin-top: 7px;
  color: #9a6b00;
  font-size: 13px;
  line-height: 17px;
}

// Visit checklist ============================================================
.visit-checklist {
  padding: 0;
  margin: 0;
  list-style: none;

  &__visit-step {
    width: calc(100% + 25px + 23px);
    margin-left: -25px;

    &--hidden {
      display: none;
    }
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .visit-checklist {
    &__visit-step {
      width: calc(100% + 25px + 22px);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .visit-checklist {
    &__visit-step {
      width: calc(100% + 15px + 13px);
      margin-left: -14px;
    }
  }
}
</style>
